import React, { useState, useEffect } from 'react';
import { adminFetch } from '../../lib/adminFetch';
import { decodeBinaryFields, bufferToBase64, isWebAuthnSupported } from '../../lib/webauthn';

// ログイン中の本人が、自分のパスキーを登録・確認・削除するモーダル。
// メール認証コードに加えて使える追加のログイン方法（置き換えではない）。
const PasskeyAdmin = ({ onClose }) => {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [message, setMessage] = useState('');
  const [labelInput, setLabelInput] = useState('');

  const load = () => {
    setLoading(true);
    adminFetch('/api/admin-passkey.php?action=list')
      .then((r) => (r.ok ? r.json() : { passkeys: [] }))
      .then((d) => setPasskeys(d.passkeys || []))
      .catch(() => setPasskeys([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const register = async () => {
    if (!isWebAuthnSupported()) { setMessage('このブラウザはパスキーに対応していません'); return; }
    setRegistering(true);
    setMessage('');
    try {
      const optRes = await adminFetch('/api/admin-passkey.php?action=register-options');
      const optData = await optRes.json();
      if (!optData.success) { setMessage(optData.message || '登録に失敗しました'); setRegistering(false); return; }

      const publicKey = decodeBinaryFields(optData.options).publicKey;
      const cred = await navigator.credentials.create({ publicKey });

      const verifyRes = await adminFetch('/api/admin-passkey.php?action=register-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientDataJSON: bufferToBase64(cred.response.clientDataJSON),
          attestationObject: bufferToBase64(cred.response.attestationObject),
          label: labelInput.trim() || 'パスキー',
        }),
      });
      const data = await verifyRes.json();
      if (data.success) {
        setMessage('パスキーを登録しました');
        setLabelInput('');
        load();
      } else {
        setMessage(data.message || '登録に失敗しました');
      }
    } catch (err) {
      setMessage(err && err.name === 'NotAllowedError' ? '登録がキャンセルされました' : '登録に失敗しました');
    }
    setRegistering(false);
  };

  const remove = async (credentialId) => {
    if (!window.confirm('このパスキーを削除しますか？')) return;
    try {
      const res = await adminFetch('/api/admin-passkey.php?action=delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credentialId }),
      });
      const data = await res.json();
      if (data.success) load();
      else setMessage(data.message || '削除に失敗しました');
    } catch { setMessage('削除に失敗しました'); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-bold">🔑 パスキー管理</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        <p className="text-xs text-gray-500 mb-4">この端末・ブラウザにパスキーを登録すると、次回からメール認証コードなしでログインできます。</p>

        {message && <div className="mb-3 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700">{message}</div>}

        <div className="mb-4 flex gap-2">
          <input type="text" value={labelInput} onChange={(e) => setLabelInput(e.target.value)}
            placeholder="名前（例：会社のPC・iPhone）任意"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-dark" />
          <button onClick={register} disabled={registering}
            className="bg-pink-dark text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-brown disabled:opacity-50 whitespace-nowrap">
            {registering ? '登録中...' : '+ 追加'}
          </button>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 text-sm py-4">読み込み中...</div>
        ) : passkeys.length === 0 ? (
          <div className="text-center text-gray-400 text-sm py-4">登録済みのパスキーはありません</div>
        ) : (
          <div className="space-y-2">
            {passkeys.map((p) => (
              <div key={p.credentialId} className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate">{p.label}</p>
                  <p className="text-[11px] text-gray-400">
                    登録: {p.createdAt ? new Date(p.createdAt).toLocaleDateString('ja-JP') : '—'}
                    {p.lastUsedAt && ` ／ 最終利用: ${new Date(p.lastUsedAt).toLocaleDateString('ja-JP')}`}
                  </p>
                </div>
                <button onClick={() => remove(p.credentialId)} className="text-red-500 hover:text-red-700 text-xs font-bold shrink-0 ml-2">削除</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PasskeyAdmin;
